import { Channel, useChannelsQuery } from 'api/ChannelsAPI';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectIsEmbeddedMessagingCoreFeatureFlagOn } from 'core/userContext';
import { paginate } from 'utils/arrayHelpers';
import { useWhatsAppMessageChannelFeature } from 'utils/hooks/featureFlags';
import { ChannelsByMessageMedium, MessageTypesByChannelId } from '../types';
import { filterChannels, formatChannelsByMessageMedium, sortChannels } from '../utils';

export const useChannels = (
  messageMedium: string,
  searchValue: string,
  currentPage: number,
  itemsPerPage: number,
  messageTypes: MessageTypesByChannelId,
) => {
  const {
    data: channels = [],
    isLoading: isLoadingChannels,
    refetch: refetchChannels,
  } = useChannelsQuery();
  const isEmbeddedOn = useSelector(selectIsEmbeddedMessagingCoreFeatureFlagOn);
  const { isWhatsAppEnabled: isWhatsAppOn } = useWhatsAppMessageChannelFeature();

  const [filteredChannels, setFilteredChannels] = useState<Channel[]>(channels);
  const [channelsByMedium, setChannelsByMedium] = useState<ChannelsByMessageMedium>({});
  const [paginatedChannels, setPaginatedChannels] = useState<Channel[]>(filteredChannels);

  // format channels by message medium
  useEffect(() => {
    if (!isLoadingChannels) {
      const sortedChannels = sortChannels(channels);
      const formattedChannels = formatChannelsByMessageMedium(
        sortedChannels,
        isEmbeddedOn,
        isWhatsAppOn,
      );
      setChannelsByMedium(formattedChannels);
    }
  }, [channels, isLoadingChannels]);

  // Filter channels based on debounced search input value
  useEffect(() => {
    const current = channelsByMedium[messageMedium];
    if (!isLoadingChannels && current) {
      const filtered = filterChannels({ channels: current, messageTypes, searchValue });
      setFilteredChannels(filtered);
    }
  }, [messageMedium, channelsByMedium, searchValue]);

  // paginate results after filtering
  useEffect(() => {
    if (!isLoadingChannels) {
      const channelsGroup = paginate(filteredChannels, itemsPerPage, currentPage);
      setPaginatedChannels(channelsGroup);
    }
  }, [filteredChannels, itemsPerPage, currentPage]);

  return {
    currentChannels: channelsByMedium[messageMedium],
    filteredChannels,
    isLoadingChannels,
    paginatedChannels,
    refetchChannels,
  };
};
